import {
  useProductsAddShowcase,
  useProductsCheckShowcase,
  useProductContainsShowcase,
} from "@/api/product/use-query";
import { useTrackingRecordSave } from "@/api/track/use-query";
import { AuthorizeContext } from "@/app/AuthorizeProvider";
import { LoginContext } from "@/app/LoginProvider";
import {
  EProductCheckSampleType,
  EProductCheckShowcaseType,
  TrackingBtnId,
  TrackingEventID,
  TrackingEventType,
} from "@/shared";
import { TProductInfo } from "@/typings";
import { isString } from "@/utils/is";
import { onErrorMessage } from "@/utils/message";
import { Message } from "@arco-design/web-react";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import {
  EventConfig,
  ReturnUseAddShowcaseProps,
  UseAddShowcaseProps,
} from "./interface";

export type { EventConfig, ReturnUseAddShowcaseProps, UseAddShowcaseProps };

export default function useAddShowcase(
  props: UseAddShowcaseProps
): ReturnUseAddShowcaseProps {
  const {
    productData,
    eventConfig,
    addedMsg = true,
    haveAppliedMsg = true,
    showcaseAdditionChecker,
    promptType = "message",
  } = props;

  const {
    mutate: fetchProductsCheckShowcase,
    isLoading: isCheckShowcaseLoading,
  } = useProductsCheckShowcase();

  const {
    mutate: fetchProductContainsShowcase,
    isLoading: isContainsShowcaseLoading,
  } = useProductContainsShowcase();

  const { mutate: fetchProductsAddShowcase, isLoading: isAddShowcaseLoading } =
    useProductsAddShowcase();

  const { mutate: fetchTrackingRecordSave } = useTrackingRecordSave();

  const loginCtx = useContext(LoginContext);
  const authorizeCtx = useContext(AuthorizeContext);

  const [isAddedShowcase, $isAddedShowcase] = useState<boolean>();

  // useEffect(() => {
  //   if (authorizeCtx?.skuVisible === false) {
  //     $isAddedShowcase(false);
  //   }
  // }, [authorizeCtx?.skuVisible]);

  /** TODO: 加入橱窗成功埋点 */
  const addShowcaseTracking = useCallback(
    (data?: TProductInfo, _eventConfig?: EventConfig) => {
      const productInfo = data || productData;
      const evc = _eventConfig || eventConfig;
      fetchTrackingRecordSave({
        uid: loginCtx?.userInfo?.uid,
        ...(productInfo && { product_id: productInfo.product_id }),
        ...evc,
        event_id: TrackingEventID.AddedShowcase,
        event_type: TrackingEventType.Exposure,
      });
    },
    [productData, eventConfig, fetchTrackingRecordSave, loginCtx?.userInfo?.uid]
  );

  /** TODO: 未授权橱窗 */
  const handleNotAuthorize = useCallback(
    (data?: TProductInfo, _eventConfig?: EventConfig) => {
      const productInfo = data || productData;
      const evc = _eventConfig || eventConfig;
      authorizeCtx?.$skuVisible?.(false);
      authorizeCtx?.$showcaseAuthVisible?.(true);
      authorizeCtx?.$product?.(productInfo);
      if (evc) {
        authorizeCtx?.$eventConfig?.(evc);
      }
    },
    [authorizeCtx, eventConfig, productData]
  );

  // 之前已加入橱窗 confirm 弹窗
  const handleHaveAppliedConfirm = useCallback(
    (product: TProductInfo, _eventConfig?: EventConfig) => {
      authorizeCtx?.$showcaseSampleGuideVisible?.(true);
      authorizeCtx?.$product?.(product);
      authorizeCtx?.$eventConfig?.(_eventConfig);
    },
    [authorizeCtx]
  );

  // 之前已加入橱窗提示
  const handleHaveAppliedPrompt = useCallback(
    (product: TProductInfo, _eventConfig?: EventConfig) => {
      if (promptType === "message") {
        if (haveAppliedMsg) {
          if (isString(haveAppliedMsg)) {
            Message.success({
              content: haveAppliedMsg,
              duration: 4000,
              closable: true,
            });
          } else {
            Message.success({
              content:
                "The item has been added to the showcase and can be seen in your personal center. You can request sample now.",
              duration: 4000,
              closable: true,
            });
          }
        }
      } else {
        handleHaveAppliedConfirm(product, _eventConfig);
      }
    },
    [haveAppliedMsg, promptType, handleHaveAppliedConfirm]
  );

  // 加入橱窗成功 confirm 弹窗
  const handleAddedConfirm = useCallback(
    (product: TProductInfo, _eventConfig?: EventConfig) => {
      authorizeCtx?.$showcaseSampleGuideVisible?.(true);
      authorizeCtx?.$product?.(product);
      authorizeCtx?.$eventConfig?.(_eventConfig);
    },
    [authorizeCtx]
  );

  /** 加入橱窗成功提示 */
  const handleAddedPrompt = useCallback(
    (product: TProductInfo, _eventConfig?: EventConfig) => {
      if (promptType === "message") {
        if (addedMsg) {
          if (isString(addedMsg)) {
            Message.success({
              content: addedMsg,
              duration: 4000,
              closable: true,
            });
          } else {
            Message.success({
              content:
                "The item has been added to the showcase and can be seen in your personal center. You can request sample now.",
              duration: 4000,
              closable: true,
            });
          }
        }
      } else {
        handleAddedConfirm(product, _eventConfig);
      }
    },
    [addedMsg, handleAddedConfirm, promptType]
  );

  /** TODO: 加入橱窗 */
  const addShowcase = useCallback(
    (data?: TProductInfo, _eventConfig?: EventConfig) => {
      const productInfo = data || productData;
      const evc = _eventConfig || eventConfig;
      if (productInfo) {
        fetchProductsAddShowcase(
          {
            options: {
              product_id: productInfo?.product_id,
              campaign_id: productInfo?.campaign_id,
            },
          },
          {
            onSuccess: (res) => {
              if (res?.code === 0) {
                // 加入橱窗成功
                $isAddedShowcase(true);
                addShowcaseTracking(data, evc);
                handleAddedPrompt(productInfo, evc);
              } else if (
                res.code === EProductCheckSampleType.NoShowcaseAuthorize
              ) {
                // 未授权橱窗
                handleNotAuthorize(productInfo, evc);
              } else {
                // 加入橱窗失败
                Message.error({ content: res?.msg });
              }
            },
            onError: onErrorMessage,
          }
        );
      }
    },
    [
      fetchProductsAddShowcase,
      productData,
      addShowcaseTracking,
      eventConfig,
      handleAddedPrompt,
      handleNotAuthorize,
    ]
  );

  /** TODO: 校验是否已添加过橱窗 */
  const checkIfAddedToShowcase = useCallback(
    (
      data?: TProductInfo,
      _eventConfig?: EventConfig,
      _showcaseAdditionChecker?: 1 | 2
    ) => {
      const productInfo = data || productData;
      const evc = _eventConfig || eventConfig;
      const showcaseChecker =
        _showcaseAdditionChecker || showcaseAdditionChecker;
      if (productInfo) {
        fetchProductContainsShowcase(
          {
            options: {
              campaign_id: productInfo?.campaign_id,
              product_id: productInfo?.product_id,
            },
          },
          {
            onSuccess: (res) => {
              // 已加入橱窗
              if (
                res?.data?.in_showcase === EProductCheckShowcaseType.InShowcase
              ) {
                $isAddedShowcase(true);
                if (!showcaseChecker) {
                  handleHaveAppliedPrompt(productInfo, evc);
                }
                // 未加入橱窗
              } else {
                if (!showcaseChecker || showcaseChecker === 2) {
                  addShowcase(data, evc);
                }
              }
            },
            onError: onErrorMessage,
          }
        );
      }
    },
    [
      fetchProductContainsShowcase,
      productData,
      addShowcase,
      eventConfig,
      handleHaveAppliedPrompt,
      showcaseAdditionChecker,
    ]
  );

  useEffect(() => {
    if (showcaseAdditionChecker && loginCtx?.userInfo) {
      checkIfAddedToShowcase(undefined, undefined, showcaseAdditionChecker);
    }
  }, [checkIfAddedToShowcase, loginCtx?.userInfo, showcaseAdditionChecker]);

  /** TODO: 点击 addShowcase 按钮埋点 */
  const addShowcaseClick = useCallback(
    (data?: TProductInfo, _eventConfig?: EventConfig) => {
      const evc = _eventConfig || eventConfig;
      fetchTrackingRecordSave({
        uid: loginCtx?.userInfo?.uid,
        ...(data && { product_id: data.product_id }),
        event_id: TrackingEventID.AddShowcase,
        event_type: TrackingEventType.Click,
        btn_id: TrackingBtnId.AddShowCase,
        ...evc,
      });
    },
    [eventConfig, loginCtx?.userInfo, fetchTrackingRecordSave]
  );

  /** TODO: 校验橱窗是否授权 */
  const checkShowcaseAuthorize = useCallback(
    (data?: TProductInfo, _eventConfig?: EventConfig) => {
      const productInfo = data || productData;
      const evc = _eventConfig || eventConfig;
      addShowcaseClick(productInfo, evc);
      if (!loginCtx?.userInfo) {
        // 未登录
        loginCtx?.$loginVisible?.(true);
        return;
      }

      fetchProductsCheckShowcase(
        {},
        {
          onSuccess: (res) => {
            if (res.code === EProductCheckSampleType.Success) {
              // 已授权橱窗
              checkIfAddedToShowcase(productInfo, evc);
            } else if (
              res.code === EProductCheckSampleType.NoShowcaseAuthorize
            ) {
              // 未授权橱窗
              handleNotAuthorize(productInfo, evc);
            }
          },
          onError: onErrorMessage,
        }
      );
    },
    [
      productData,
      eventConfig,
      addShowcaseClick,
      loginCtx,
      fetchProductsCheckShowcase,
      checkIfAddedToShowcase,
      handleNotAuthorize,
    ]
  );

  const loading = useMemo(() => {
    return (
      isCheckShowcaseLoading ||
      isContainsShowcaseLoading ||
      isAddShowcaseLoading
    );
  }, [isCheckShowcaseLoading, isContainsShowcaseLoading, isAddShowcaseLoading]);

  return {
    checkShowcaseAuthorize,
    checkIfAddedToShowcase,
    addShowcase,
    loading,
    isAddedShowcase,
  };
}
